/** Recurring expense / premium billing frequencies used for monthly rollups. */
export type ExpenseFrequency = 'weekly' | 'monthly' | 'quarterly' | 'annual';

/** Compliance billing_frequency plus expense frequencies; one_off is not recurring. */
export type MonthlyRollFrequency = ExpenseFrequency | 'one_off';

/**
 * Normalise a billed amount to a monthly equivalent (integer pence).
 * - weekly → ×52/12
 * - monthly → as-is
 * - quarterly → /3
 * - annual → /12
 * - one_off → 0 (not a recurring monthly cost)
 */
export function monthlyEquivalentPence(
  amountPence: number | null | undefined,
  frequency: MonthlyRollFrequency | string | null | undefined,
): number {
  if (amountPence == null || !Number.isFinite(amountPence) || amountPence < 0) {
    return 0;
  }
  switch (frequency) {
    case 'weekly':
      return Math.round((amountPence * 52) / 12);
    case 'monthly':
      return Math.round(amountPence);
    case 'quarterly':
      return Math.round(amountPence / 3);
    case 'annual':
      return Math.round(amountPence / 12);
    case 'one_off':
    default:
      return 0;
  }
}

export function sumMonthlyEquivalentPence(
  rows: Array<{
    amount_pence?: number | null;
    cost_pence?: number | null;
    frequency?: string | null;
    billing_frequency?: string | null;
  }>,
): number {
  let total = 0;
  for (const r of rows) {
    const amount = r.amount_pence ?? r.cost_pence ?? null;
    const freq = r.frequency ?? r.billing_frequency ?? null;
    total += monthlyEquivalentPence(amount, freq);
  }
  return total;
}
